/**
 * A window's place as plain data (P1.9-T97, ADR-061's amendment "a window can be moved"): the viewer's wish, the
 * window's left and top in the page's logical area; what of it fits a room, so a window is never lost off the page;
 * what a drag of the title bar and the arrow keys do to it; and the text it is kept as. Every place is whole logical
 * pixels. A window never moved has no wish (null) and stands where its layout puts it. Pure: the page holds the wish
 * and hands it in.
 *
 * The same file holds a window's size (P1.9-T181, ADR-061's amendment "every game window moves and resizes", items 4
 * to 8, 10 and 11): the size wish and the text it is kept as, the limits each framed window has, the room every window
 * is held in, what a drag of an edge or a corner and the grip's keys do to a box, and the rule that a window has no
 * frame where the room cannot hold its minimum.
 */

/**
 * A window's left and top from the area's top left, in logical pixels.
 */
export interface WindowPlace {
  readonly left: number;
  readonly top: number;
}

/**
 * A window's size in logical pixels.
 */
export interface PlaceSize {
  readonly width: number;
  readonly height: number;
}

/**
 * The room a window is held inside: its edges from the area's top left, in whole logical pixels.
 */
export interface PlaceRoom {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * A move on each axis in whole logical pixels, right and down positive.
 */
export interface PlaceStep {
  readonly x: number;
  readonly y: number;
}

/**
 * How far an arrow key moves a window, and how far with Shift held, in logical pixels: the chat grip's steps.
 */
export const PLACE_KEY_STEP = { plain: 1, shift: 8 } as const;

/**
 * A kept place's form: two runs of digits around a comma.
 */
const KEPT = /^(\d{1,5}),(\d{1,5})$/;

/**
 * The room a page gives its windows: its area less a margin on each side.
 *
 * @param area The area's size in logical pixels, as measured; a fraction of a pixel is dropped.
 * @param margin The margin kept on each side.
 * @param top The room's top edge where the page keeps a band above it; the margin unless given.
 * @returns The room.
 * @example
 * pageRoom({ width: 640, height: 360 }, 4);
 * // { left: 4, top: 4, right: 636, bottom: 356 }
 */
export function pageRoom(area: PlaceSize, margin: number, top: number = margin): PlaceRoom {
  return { left: margin, top, right: Math.floor(area.width) - margin, bottom: Math.floor(area.height) - margin };
}

/**
 * One axis of a place held inside the room: on a whole pixel, between the room's near edge and the farthest the
 * window's size lets it start; the near edge when the window is larger than the room or the place is no number.
 *
 * @param at The place on the axis.
 * @param size The window's size on the axis.
 * @param near The room's near edge.
 * @param far The room's far edge.
 * @returns The place to draw on the axis.
 */
function hold(at: number, size: number, near: number, far: number): number {
  return Number.isFinite(at) ? Math.max(near, Math.min(far - size, Math.round(at))) : near;
}

/**
 * Where a wish is drawn: the place held inside the room on whole logical pixels, so the window is whole on the page;
 * where the window is larger than the room on an axis its left or top edge stands at the room's, so its title bar
 * never leaves. The wish itself is not changed, so a page made larger again shows the window where it was left.
 *
 * @param wish The place wished for.
 * @param size The window's size.
 * @param room The room.
 * @returns The place to draw.
 * @example
 * clampPlace({ left: 600, top: 300 }, { width: 300, height: 236 }, { left: 4, top: 4, right: 636, bottom: 356 });
 * // { left: 336, top: 120 }
 */
export function clampPlace(wish: WindowPlace, size: PlaceSize, room: PlaceRoom): WindowPlace {
  return { left: hold(wish.left, size.width, room.left, room.right), top: hold(wish.top, size.height, room.top, room.bottom) };
}

/**
 * The wish after the window moved by a step on each axis from where it is drawn. An axis that did not move keeps its
 * wish. An axis that moved takes the drawn place plus the step, held inside the room; where that stops at an edge of
 * the room while the wish lies beyond that edge, the wish stays, so pushing against a small page never loses where a
 * larger page will show the window. A window never moved that ends where its layout puts it stays never moved.
 *
 * @param wish The place kept, or null for a window never moved.
 * @param home Where the window's layout puts it, from which a window never moved starts.
 * @param step How far it moved on each axis, in whole logical pixels.
 * @param size The window's size.
 * @param room The room.
 * @returns The place to keep, or null for a window still never moved.
 * @example
 * movePlace({ left: 900, top: 44 }, { left: 326, top: 44 }, { x: -5, y: 0 }, { width: 300, height: 236 }, { left: 4, top: 4, right: 636, bottom: 356 });
 * // { left: 331, top: 44 }
 */
export function movePlace(wish: WindowPlace | null, home: WindowPlace, step: PlaceStep, size: PlaceSize, room: PlaceRoom): WindowPlace | null {
  const kept = wish ?? home;
  const drawn = clampPlace(kept, size, room);
  /**
   * One axis of the wish.
   *
   * @param was The wish on the axis.
   * @param by The step on the axis.
   * @param from The drawn place on the axis.
   * @param near The room's near edge.
   * @param far The farthest the window may start on the axis, never before the near edge.
   * @returns The wish to keep on the axis.
   */
  const axis = (was: number, by: number, from: number, near: number, far: number): number => {
    if (by === 0) {
      return was;
    }
    const next = Math.max(near, Math.min(far, from + by));
    return (next === far && was > far) || (next === near && was < near) ? was : next;
  };
  const next = {
    left: axis(kept.left, step.x, drawn.left, room.left, Math.max(room.left, room.right - size.width)),
    top: axis(kept.top, step.y, drawn.top, room.top, Math.max(room.top, room.bottom - size.height)),
  };
  return wish === null && next.left === home.left && next.top === home.top ? null : next;
}

/**
 * The wish a drag of the title bar gives: the window moved by the pointer's travel in logical pixels, rounded, so it
 * is only ever drawn on whole logical pixels, from where it was drawn as the drag began (`movePlace`).
 *
 * @param wish The place kept as the drag began, or null for a window never moved.
 * @param home Where the window's layout puts it.
 * @param travel How far the pointer has moved since, in CSS pixels, right and down positive.
 * @param px CSS pixels to the logical pixel.
 * @param size The window's size.
 * @param room The room.
 * @returns The place to keep, or null for a window still never moved.
 * @example
 * dragPlace(null, { left: 326, top: 44 }, { x: -40, y: 20 }, 2, { width: 300, height: 236 }, { left: 4, top: 4, right: 636, bottom: 356 });
 * // { left: 306, top: 54 }
 */
export function dragPlace(wish: WindowPlace | null, home: WindowPlace, travel: PlaceStep, px: number, size: PlaceSize, room: PlaceRoom): WindowPlace | null {
  return movePlace(wish, home, { x: Math.round(travel.x / px), y: Math.round(travel.y / px) }, size, room);
}

/**
 * The step a key of the focused title gives: the arrow keys, a logical pixel and 8 with Shift held.
 *
 * @param key The key, as `KeyboardEvent.key` names it.
 * @param shift Whether Shift is held.
 * @returns The step, or null for a key that moves nothing.
 * @example
 * keyStep("ArrowUp", true);
 * // { x: 0, y: -8 }
 */
export function keyStep(key: string, shift: boolean): PlaceStep | null {
  const by = shift ? PLACE_KEY_STEP.shift : PLACE_KEY_STEP.plain;
  const steps: Readonly<Record<string, PlaceStep>> = {
    ArrowRight: { x: by, y: 0 },
    ArrowLeft: { x: -by, y: 0 },
    ArrowDown: { x: 0, y: by },
    ArrowUp: { x: 0, y: -by },
  };
  return Object.hasOwn(steps, key) ? (steps[key] ?? null) : null;
}

/**
 * A place as it is kept.
 *
 * @param place The place, or null for a window never moved.
 * @returns "LEFT,TOP", or the empty text for none.
 */
export function formatPlace(place: WindowPlace | null): string {
  return place === null ? "" : `${place.left},${place.top}`;
}

/**
 * Reads a kept value as a place.
 *
 * @param raw The kept text, or null when nothing is kept.
 * @returns The place it names, or null for anything that is not two whole numbers around a comma.
 */
export function parsePlace(raw: string | null): WindowPlace | null {
  const match = raw === null ? null : KEPT.exec(raw);
  return match === null ? null : { left: Number(match[1]), top: Number(match[2]) };
}

/**
 * A window's box: its place and its size, in whole logical pixels from the area's top left.
 */
export interface FrameBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * The edges and corners a resize starts from.
 */
export type ResizeEdge = "top" | "right" | "bottom" | "left" | "topLeft" | "topRight" | "bottomRight" | "bottomLeft";

/**
 * A framed window's limits in logical pixels: the smallest size at which its layout still holds every control, the
 * largest the table allows, and, for a window whose body is a grid of slots, the step its size moves in on each axis
 * (an axis of 0 is free; null is free on both). A size is on the lattice the step makes from the minimum.
 */
export interface WindowLimits {
  readonly min: PlaceSize;
  readonly max: PlaceSize;
  readonly step: PlaceSize | null;
}

/**
 * The room every window is held inside, whatever its kind: the HUD's margin on each side and a top that clears the
 * staff Feedback band, in logical pixels.
 */
export const WINDOW_ROOM = { margin: 4, top: 32 } as const;

/**
 * Every framed window's limits, by the id the page gives its window. A window with no entry has no frame. The
 * slot-grid windows step by their slot's pitch (a 24 px slot and a 1 px gap in the bag, a 24 px row and a 2 px gap in
 * the chest) and hold their design sizes on the lattice: the bag's 300 by 236 and the chest's heights from 103 by 26.
 * The map's minimum is its design size, where its phone layout begins; the float's 112 by 112 is ADR-100 decision 49 and its largest, 950 by 977, the field the `nearby` crop covers at the finest scale with the tallest legend (decision 45; `float.test.ts` holds it equal to `mapWindowMost`).
 * The system windows' minimums are measured on their laid out bodies (P1.9-T184, `tests/browser/system-window-limits.test.ts`):
 * Settings 420 by 200, the Look window 320 by 240, the Moderation window's narrow layout 300 by 236 and the Feedback
 * form 220 by 280, each the smallest size at which no control or label is clipped and the smallest that keeps the
 * phone floor (a 445 by 230 page, whose room is 437 by 194) without a frame. The character window's 300 by 236 is
 * the design's play window (ADR-138's amendment of 2026-10-31, decision 1).
 */
export const WINDOW_LIMITS = {
  inventory: { min: { width: 200, height: 161 }, max: { width: 450, height: 436 }, step: { width: 25, height: 25 } },
  chest: { min: { width: 150, height: 103 }, max: { width: 300, height: 415 }, step: { width: 0, height: 26 } },
  settings: { min: { width: 420, height: 200 }, max: { width: 900, height: 520 }, step: null },
  moderation: { min: { width: 300, height: 236 }, max: { width: 900, height: 520 }, step: null },
  feedback: { min: { width: 220, height: 280 }, max: { width: 420, height: 520 }, step: null },
  look: { min: { width: 320, height: 240 }, max: { width: 900, height: 600 }, step: null },
  map: { min: { width: 480, height: 300 }, max: { width: 1020, height: 1020 }, step: null },
  mapFloat: { min: { width: 112, height: 112 }, max: { width: 950, height: 977 }, step: null },
  character: { min: { width: 300, height: 236 }, max: { width: 640, height: 420 }, step: null },
} as const satisfies Record<string, WindowLimits>;

/**
 * The id of a window that has a frame.
 */
export type FramedWindowId = keyof typeof WINDOW_LIMITS;

/**
 * A kept size's form: two runs of digits around an x, the chat's.
 */
const KEPT_SIZE = /^(\d{1,4})x(\d{1,4})$/;

/**
 * The room a game window is held inside on a page.
 *
 * @param area The area's size in logical pixels, as measured; a fraction of a pixel is dropped.
 * @returns The room: the area less 4 on each side, its top at 32.
 * @example
 * windowRoom({ width: 640, height: 360 });
 * // { left: 4, top: 32, right: 636, bottom: 356 }
 */
export function windowRoom(area: PlaceSize): PlaceRoom {
  return pageRoom(area, WINDOW_ROOM.margin, WINDOW_ROOM.top);
}

/**
 * Whether a window has a frame in a room: it does where the room holds its minimum on both axes. Where it does not
 * (the narrow and phone layouts) the window is the whole screen as its layout makes it, with nothing to move or size.
 *
 * @param id The window.
 * @param room The room.
 * @returns True when the room is at least the window's minimum wide and tall.
 */
export function hasWindowFrame(id: FramedWindowId, room: PlaceRoom): boolean {
  const { min } = WINDOW_LIMITS[id];
  return room.right - room.left >= min.width && room.bottom - room.top >= min.height;
}

/**
 * One axis of a size held between its minimum and a cap, on the lattice a step makes from the minimum: the value
 * rounded to the nearest step, and one step less where that passes the cap. The minimum wins where the cap is below it.
 *
 * @param value The size wished for on the axis.
 * @param min The least.
 * @param cap The most: the smaller of the maximum and the room.
 * @param step The step on the axis, or 0 for none.
 * @returns The size on the axis.
 */
function fitAxis(value: number, min: number, cap: number, step: number): number {
  const most = Math.max(min, cap);
  if (!Number.isFinite(value)) {
    return min;
  }
  const held = Math.max(min, Math.min(most, Math.round(value)));
  if (step <= 0) {
    return held;
  }
  const near = min + Math.round((held - min) / step) * step;
  return near > most ? near - step : near;
}

/**
 * Where a size is drawn: the wish held between the limits' minimum and the smaller of their maximum and the room, on
 * whole pixels and on the step's lattice. A page too small for the wish draws it smaller, to the minimum at least, and
 * a larger page draws it again. The wish itself is not changed.
 *
 * @param wish The size wished for.
 * @param limits The window's limits.
 * @param room The room, or null to hold the limits alone, as reading a kept size does.
 * @returns The size to draw.
 * @example
 * clampSize({ width: 800, height: 800 }, { min: { width: 200, height: 120 }, max: { width: 900, height: 900 }, step: null }, { left: 4, top: 32, right: 636, bottom: 356 });
 * // { width: 632, height: 324 }
 */
export function clampSize(wish: PlaceSize, limits: WindowLimits, room: PlaceRoom | null): PlaceSize {
  const across = room === null ? Number.POSITIVE_INFINITY : room.right - room.left;
  const down = room === null ? Number.POSITIVE_INFINITY : room.bottom - room.top;
  return {
    width: fitAxis(wish.width, limits.min.width, Math.min(limits.max.width, across), limits.step?.width ?? 0),
    height: fitAxis(wish.height, limits.min.height, Math.min(limits.max.height, down), limits.step?.height ?? 0),
  };
}

/**
 * One axis of a resize: the edge being dragged follows the pointer by the travel, the opposite edge stays where it
 * was, and the size is held to the limits, the room and the step. A drag that reaches a limit holds the size, so the
 * near edge, worked from the fixed one, never slides.
 *
 * @param near The box's near edge on the axis (left or top).
 * @param size The box's size on the axis.
 * @param travel The pointer's travel on the axis, whole pixels.
 * @param dragsFar True when the far edge (right or bottom) is the one dragged, false for the near edge.
 * @param roomNear The room's near edge.
 * @param roomFar The room's far edge.
 * @param min The least size.
 * @param max The most size.
 * @param step The step, or 0 for none.
 * @returns The near edge and the size on the axis.
 */
function resizeAxis(near: number, size: number, travel: number, dragsFar: boolean, roomNear: number, roomFar: number, min: number, max: number, step: number): { near: number; size: number } {
  if (dragsFar) {
    return { near, size: fitAxis(size + travel, min, Math.min(max, roomFar - near), step) };
  }
  const far = near + size;
  const fitted = fitAxis(size - travel, min, Math.min(max, far - roomNear), step);
  return { near: far - fitted, size: fitted };
}

/**
 * The box a drag of an edge or a corner gives: the dragged edges follow the pointer's travel and the opposite edges
 * stay still, so a window is never pushed by its own resizing; the size is held between the limits' minimum and the
 * smaller of their maximum and the room, on the step's lattice, and a drag at a limit holds the box where it is.
 *
 * @param edge The edge or corner the press began on.
 * @param start The box as the drag began (the drawn box, not the wish).
 * @param travel How far the pointer has moved since, in logical pixels, right and down positive; rounded to whole pixels.
 * @param limits The window's limits.
 * @param room The room.
 * @returns The new box.
 * @example
 * resizeBox("topLeft", { left: 100, top: 60, width: 300, height: 200 }, { x: 20, y: 10 }, { min: { width: 200, height: 120 }, max: { width: 400, height: 300 }, step: null }, { left: 4, top: 32, right: 636, bottom: 356 });
 * // { left: 120, top: 70, width: 280, height: 190 }
 */
export function resizeBox(edge: ResizeEdge, start: FrameBox, travel: PlaceStep, limits: WindowLimits, room: PlaceRoom): FrameBox {
  const movesLeft = edge === "left" || edge === "topLeft" || edge === "bottomLeft";
  const movesRight = edge === "right" || edge === "topRight" || edge === "bottomRight";
  const movesTop = edge === "top" || edge === "topLeft" || edge === "topRight";
  const movesBottom = edge === "bottom" || edge === "bottomLeft" || edge === "bottomRight";
  const x = movesLeft || movesRight ? resizeAxis(start.left, start.width, Math.round(travel.x), movesRight, room.left, room.right, limits.min.width, limits.max.width, limits.step?.width ?? 0) : { near: start.left, size: start.width };
  const y = movesTop || movesBottom ? resizeAxis(start.top, start.height, Math.round(travel.y), movesBottom, room.top, room.bottom, limits.min.height, limits.max.height, limits.step?.height ?? 0) : { near: start.top, size: start.height };
  return { left: x.near, top: y.near, width: x.size, height: y.size };
}

/**
 * The change of size a key of the focused grip gives: the arrow keys, a logical pixel and 8 with Shift held, and on an
 * axis with a step one step whatever Shift is. Applied as the travel of the bottom right corner (`resizeBox`).
 *
 * @param key The key, as `KeyboardEvent.key` names it.
 * @param shift Whether Shift is held.
 * @param step The window's step, or null for none.
 * @returns The change on each axis, or null for a key that sizes nothing.
 * @example
 * keySize("ArrowRight", false, { width: 25, height: 25 });
 * // { x: 25, y: 0 }
 */
export function keySize(key: string, shift: boolean, step: PlaceSize | null): PlaceStep | null {
  const by = keyStep(key, shift);
  if (by === null) {
    return null;
  }
  const along = (amount: number, pitch: number): number => (amount === 0 || pitch <= 0 ? amount : Math.sign(amount) * pitch);
  return { x: along(by.x, step?.width ?? 0), y: along(by.y, step?.height ?? 0) };
}

/**
 * A size as it is kept.
 *
 * @param size The size wished for, or null for a window never resized.
 * @returns "WIDTHxHEIGHT", or the empty text for none.
 */
export function formatSize(size: PlaceSize | null): string {
  return size === null ? "" : `${size.width}x${size.height}`;
}

/**
 * Reads a kept value as a size wish, held to the window's limits (not to a room, which is the page's at the time it is
 * drawn).
 *
 * @param raw The kept text, or null when nothing is kept.
 * @param limits The window's limits.
 * @returns The wish, clamped to the limits and the step; null for anything that is not two whole numbers around an x.
 */
export function parseSize(raw: string | null, limits: WindowLimits): PlaceSize | null {
  const match = raw === null ? null : KEPT_SIZE.exec(raw);
  return match === null ? null : clampSize({ width: Number(match[1]), height: Number(match[2]) }, limits, null);
}

/**
 * What the viewer wished for a window: where it stands and how large it is; null for a window never moved or never
 * resized.
 */
export interface FrameWishes {
  readonly place: WindowPlace | null;
  readonly size: PlaceSize | null;
}

/**
 * The box a framed window is drawn at: its layout's own size where it was never resized and its layout's own place
 * where it was never moved, otherwise the wishes held to the window's limits and the room, the place whole inside
 * the room, so the title bar is always on the page. A window with neither wish is its layout's box as it stands, so
 * a default place that lies outside the room (a window at 4 under the staff band) is not moved by this rule. A size
 * wish alone never moves the layout's place off the room's near edges (a resize never moves the window's top left
 * unless the room forces it), and moves it only as far as its far edges need to stay inside; a place wish is always
 * held inside the room. A window with no frame in this room (the narrow and phone
 * layouts) is drawn as its layout makes it and neither wish is read. The wishes are not changed.
 *
 * @param id The window.
 * @param home The box its layout gives.
 * @param wishes The place and the size wished for.
 * @param room The room.
 * @returns The box to draw.
 * @example
 * drawFrame("inventory", { left: 326, top: 44, width: 300, height: 236 }, { place: null, size: null }, { left: 4, top: 32, right: 636, bottom: 356 });
 * // { left: 326, top: 44, width: 300, height: 236 }
 */
export function drawFrame(id: FramedWindowId, home: FrameBox, wishes: FrameWishes, room: PlaceRoom): FrameBox {
  if (!hasWindowFrame(id, room)) {
    return home;
  }
  const size = wishes.size === null ? { width: home.width, height: home.height } : clampSize(wishes.size, WINDOW_LIMITS[id], room);
  if (wishes.place === null && wishes.size === null) {
    return home;
  }
  const held = clampPlace(wishes.place ?? home, size, room);
  const place = wishes.place === null ? { left: Math.min(home.left, held.left), top: Math.min(home.top, held.top) } : held;
  return { left: place.left, top: place.top, width: size.width, height: size.height };
}

/**
 * The wishes to keep after a resize took a window from the box it was drawn at to another: the place changes only
 * where the left or top edge moved, the size only where the width or height did, so a drag of the far edges never
 * writes a place and a drag that changes nothing writes nothing. What would be written as the layout's own place or
 * size, with nothing kept before, stays unkept.
 *
 * @param before The wishes as the resize began.
 * @param home The box the window's layout gives.
 * @param drawn The box as the resize began.
 * @param next The box the resize gave.
 * @returns The wishes to keep.
 */
export function resizeWishes(before: FrameWishes, home: FrameBox, drawn: FrameBox, next: FrameBox): FrameWishes {
  const moved = next.left !== drawn.left || next.top !== drawn.top;
  const sized = next.width !== drawn.width || next.height !== drawn.height;
  const place = !moved || (before.place === null && next.left === home.left && next.top === home.top) ? before.place : { left: next.left, top: next.top };
  const size = !sized || (before.size === null && next.width === home.width && next.height === home.height) ? before.size : { width: next.width, height: next.height };
  return { place, size };
}

/**
 * The box a key of the focused grip gives: the bottom right corner moved by `keySize`'s step, the top left held.
 *
 * @param key The key, as `KeyboardEvent.key` names it.
 * @param shift Whether Shift is held.
 * @param drawn The box as drawn.
 * @param limits The window's limits.
 * @param room The room.
 * @returns The new box, or null for a key that sizes nothing.
 * @example
 * keyResizeBox("ArrowRight", false, { left: 100, top: 60, width: 300, height: 200 }, { min: { width: 200, height: 120 }, max: { width: 400, height: 300 }, step: null }, { left: 4, top: 32, right: 636, bottom: 356 });
 * // { left: 100, top: 60, width: 301, height: 200 }
 */
export function keyResizeBox(key: string, shift: boolean, drawn: FrameBox, limits: WindowLimits, room: PlaceRoom): FrameBox | null {
  const travel = keySize(key, shift, limits.step);
  return travel === null ? null : resizeBox("bottomRight", drawn, travel, limits, room);
}

/**
 * A size as the window shows it while it is resized, the chat's form.
 *
 * @param size The size drawn.
 * @returns "WIDTH x HEIGHT".
 */
export function sizeLabel(size: PlaceSize): string {
  return `${size.width} x ${size.height}`;
}
